import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Data, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, noop } from 'rxjs';

import { FilterListItemValueModel, FilterListItemModel, FilterListItemChangeArgs, TsOverlayComponent } from 'tsui';
import { ColumnDef, DEFAULT_GRID_OPTIONS } from 'tsui/lib/ts-table/ts-table.model';
import { AccountService } from 'app/core';
import { FieldDef, SavedView, QueryConfig } from 'app/core/models';
import { LookupService } from '@services/lookup.service';
import { TsFilterUtilsService } from '@services/ts-filter-utils.service';
import { TableStatusRenderer } from '@shared/components/ts-table-renderers/table-status-renderer/table-status-renderer.component';
import { ChangeType, OriginalStates, TableSortState } from '@shared/models/state-tracking.model';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { SortChangedEvent } from 'ag-grid-enterprise';
import { Project, Property } from 'app/modules/projects/projects.types';
import { TsSnackbarService, TsTableService } from 'tsui';
import { TsTableHeaderComponent } from 'tsui/lib/ts-table';
import { CommonTableStylingFunctions } from '@shared/functions/common-table-styling.functions';

export interface TablePagination {
  length: number;
  size: number;
  page: number;
  lastPage: number;
  startIndex: number;
  endIndex: number;
}

@Component({
  selector: 'filter-table',
  templateUrl: './filter-table.component.html',
  styleUrls: ['./filter-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterTableComponent {
  @Input() title: string = 'Default';
  @Input() data: Data;
  @Input() relatedToType: string;
  @Input() savedView: SavedView;
  @Input() fieldDefs: FieldDef[];
  @Input() queryFilters: Partial<QueryConfig.QueryFilters>;
  @Input() allowCsvExport: boolean = false;
  @Input() set displayColumns(value: string[]) {
    this._displayColumns = value;
    this.getColDefs();
    this._changeDetectorRef.markForCheck();
  }
  @Input() dataset: Property[] | Project[];
  @Input() sortState: TableSortState;
  @Input() canAddDeal: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() tableIsLoading: boolean = false;
  @Input() addNewObjectConfig: { label: string; tooltip: string };
  @Input() useFieldSelector: boolean = false;

  @Input() set pagination(data) {
    if (data) this._pagination = data;
  }

  @Output() pageEvent = new EventEmitter<any>();
  @Output() sortEvent = new EventEmitter<any>();
  @Output() filterEvent = new EventEmitter<any>();
  @Output() searchEvent = new EventEmitter<any>();
  @Output() updateEvent = new EventEmitter<any>();
  @Output() exportCsvEvent = new EventEmitter<void>();
  @Output() dealAdded = new EventEmitter<'add' | 'link'>();
  @Output() addNewObjectEvent = new EventEmitter<void>();
  @Output() saveColumnsEvent = new EventEmitter<string[]>();
  @Output() saveSortEvent = new EventEmitter<TableSortState>();
  @Output() columnsChange = new EventEmitter<string[]>();

  @ViewChild('filtersOverlayBox') private _filtersOverlayBox!: TsOverlayComponent;

  private _displayColumns: string[] = [];
  private _pagination: TablePagination;
  private readonly destroyRef = inject(DestroyRef);
  private _changeTriggers: Set<ChangeType> = new Set();
  private _originalStates: OriginalStates;

  searchInputControl: FormControl = new FormControl('');
  columnDefs: ColumnDef[] = [];
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  gridOptions = {
    ...DEFAULT_GRID_OPTIONS,
    animateRows: true,
    suppressAggAtRootLevel: true,
    loading: false,
  };

  get displayColumns(): string[] {
    return this._displayColumns;
  }

  get pagination(): TablePagination {
    return this._pagination;
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  constructor(
    private _accountService: AccountService,
    private _changeDetectorRef: ChangeDetectorRef,
    public readonly lookupService: LookupService,
    private readonly tsFilterUtilsService: TsFilterUtilsService,
    private router: Router,
    private readonly _snackBar: TsSnackbarService,
  ) {
    this.pagination = {
      length: 1,
      size: 1,
      page: 1,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };
  }

  ngOnInit(): void {
    this.getColDefs();
    this.setFilterFields();
    this.searchInputWatcher();
  }

  private searchInputWatcher(): void {
    this.searchInputControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), TsRxjsOperators.memoizedDebounceTime(500))
      .subscribe((query) => {
        this.queryFilters = { ...this.queryFilters, q: query };
        this.searching(this.queryFilters);
      });
  }

  private saveTableSort(): void {
    if (!this.savedView) return;

    const sortDefinition = { active: this.sortState.active, direction: this.sortState.direction };

    this.savedView.definition = JSON.stringify({
      table: {
        fields: JSON.parse(this.savedView.definition).table?.fields,
        sort: sortDefinition,
      },
    });
    this._accountService.updateSavedView(this.savedView).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  private saveColumns(): void {
    if (!this.savedView) return;

    this.savedView.definition = JSON.stringify({
      table: { fields: this.displayColumns, sort: JSON.parse(this.savedView.definition).table?.sort },
    });
    this._accountService.updateSavedView(this.savedView).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  private getColDefs(): void {
    if (this.displayColumns?.length) {
      this.columnDefs = this.displayColumns.map((column) => this.lookupField(column)).filter((def) => !!def);
    } else {
      this.columnDefs = [];
    }
    this.setColumnsAttributes();
  }

  private setColumnsAttributes(): void {
    this.columnDefs.map((fieldDef) => {
      if (fieldDef) {
        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          headerComponentParams: {
            innerHeaderComponent: TsTableHeaderComponent,
            innerHeaderComponentParams: {
              label: fieldDef.label,
              readonly: fieldDef.meta?.readonly,
            },
          },
          sortable: true,
          resizable: true,
          sort: this.sortState?.active === fieldDef.name ? this.sortState?.direction : null,
          editable: (params) => {
            if (fieldDef.meta?.readonly) {
              return false;
            }
            if (params.data?.permissions?.is_editable === false) {
              return false;
            }
            if (fieldDef.locked) {
              return false;
            }
            return true;
          },
          cellStyle: (params) => {
            return CommonTableStylingFunctions.applyConditionalStyling(params);
          },
        };

        if (fieldDef.name === 'workflow_state') {
          fieldDef.tsTableOptions.cellRenderer = TableStatusRenderer;
          fieldDef.tsTableOptions.cellRendererParams = {
            statusColors: {
              draft: '#6B7280',
              submitted: '#3F83F8',
              rejected: '#F05252',
              accepted: '#0E9F6E',
            },
          };
        }
      }
    });
  }

  private setFilterFields(): void {
    this.typeFields.next(this.tsFilterUtilsService.getFilterList(this.fieldDefs));
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  public addDeal(mode: 'add' | 'link'): void {
    this.dealAdded.emit(mode);
  }

  public onSortChanged(event: SortChangedEvent): void {
    if (event.columns && event.columns.length > 0) {
      const sortedColumn = event.columns.filter((col) => col.getSort());
      this.sortState = {
        active: sortedColumn[0]?.getColDef().field || 'name',
        direction: sortedColumn[0]?.getSort() || 'asc',
      };
    } else {
      this.sortState = {
        active: 'name',
        direction: 'asc',
      };
    }

    this.sort(this.sortState);
  }

  public onColumnMoved(event: any): void {
    TsTableService.moveColumns(
      event,
      this.columnDefs.map((x) => x.name.toLowerCase()),
      ['openDetails'],
      (columns: string[]) => {
        this.displayColumns = columns;
        this.openTrackStateSnackbar('columns');
        this.getColDefs();
      },
    );
  }

  public handleOpenDetails(data: any) {
    if (this.relatedToType == 'Project') {
      this.router.navigate(['/projects/' + data.pipeline_id + '/' + data.id]);
    } else if (this.relatedToType == 'Property') {
      this.router.navigate(['/properties/' + data.id]);
    } else if (this.relatedToType == 'PropertyTenant') {
      this.router.navigateByUrl(`/space/void?return=/space/leases/0/${data.id}&backUrl=${this.router.url}`, {
        skipLocationChange: true,
      });
    }
  }

  public lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  public onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    this.filterValueList.next([]);
    this.queryFilters = { fjson: `[]`, q: '' };
    this.filtering(this.queryFilters);
  }

  public onFilterApplied(): void {
    this.queryFilters = {
      ...this.queryFilters,
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
      q: '',
    };

    this.filtering(this.queryFilters);

    this._filtersOverlayBox.closeOverlay();
  }

  public onUpdate(params, item): void {
    this.updateEvent.emit({ params: params, item: item });
  }

  public onCellEditingStopped(event): void {
    if (event.data && event.oldValue !== event.newValue) {
      const params = {
        [event.colDef.field]: event.newValue,
      };
      this.updateEvent.emit({ params, item: event.data });
    }
  }

  public page(event): void {
    this.pageEvent.emit({ ...event, pageIndex: event.activePage - 1 });
  }

  public sort(event): void {
    this.sortEvent.emit(event);
    this.openTrackStateSnackbar('sort');
  }

  public searching(event: Partial<QueryConfig.QueryFilters>): void {
    this.searchEvent.emit(event);
  }

  public filtering(event: Partial<QueryConfig.QueryFilters>): void {
    this.filterEvent.emit(event);
  }

  public exportCsv(): void {
    this.exportCsvEvent.emit();
  }

  public onColumnsChanged(newColumns: string[]): void {
    this.displayColumns = newColumns;
    this.openTrackStateSnackbar('columns');
    this.getColDefs();
    this._changeDetectorRef.markForCheck();
    this.columnsChange.emit(this.displayColumns);
  }

  public addNewObject(): void {
    this.addNewObjectEvent.emit();
  }

  private openTrackStateSnackbar(changeType: ChangeType): void {
    this._changeTriggers.add(changeType);
    this.captureOriginalStates();

    this._snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
  }

  private captureOriginalStates(): void {
    this._originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      sort: this.getSortStateState(),
    };
  }

  private getDisplayColumnsState(): string[] {
    return this.savedView?.definition
      ? (JSON.parse(this.savedView.definition).table?.fields ?? this.displayColumns)
      : this.displayColumns;
  }

  private getSortStateState(): TableSortState {
    return this.savedView?.definition
      ? (JSON.parse(this.savedView.definition).table?.sort ?? this.sortState)
      : this.sortState;
  }

  private resetToOriginalStates(): void {
    if (this._changeTriggers.has('columns')) {
      this.displayColumns = this.getDisplayColumnsState();
      this.getColDefs();
      this.saveColumns();
      this.columnsChange.emit(this.displayColumns);
    }

    if (this._changeTriggers.has('sort')) {
      this.sortState = this._originalStates?.sort ?? this.sortState;
      this.sort(this.sortState);
      this.saveTableSort();
    }

    this.clearChangeTriggers();
    this._changeDetectorRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this._changeTriggers.has('columns')) {
      this.saveColumns();
      this.saveColumnsEvent.emit(this.displayColumns);
    }

    if (this._changeTriggers.has('sort')) {
      this.saveTableSort();
      this.saveSortEvent.emit(this.sortState);
    }

    this.clearChangeTriggers();
    this._changeDetectorRef.markForCheck();
  }

  private clearChangeTriggers(): void {
    this._changeTriggers.clear();
  }
}
